import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, Blocks, Bot, PackageSearch, Check, CheckCircle2, ClipboardList, Container, PackageCheck, Clock, Clock3, Gauge, Mail, MapPinCheck, Minus, Plug, Puzzle, RotateCcw, Store, TrendingUp, type LucideIcon, Factory } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, Skeleton } from "@/components/ui";
import { api, ApiError, canManage, type ModuleKey, type ModulePackageInfo, type ModulePrice, type TenantModule } from "@/lib/api";
import { fmtNumber } from "@/lib/format";
import { BundlesSection } from "@/components/ModuleBundles";
import { intlLocale, t } from "@/lib/i18n";

const ICON: Record<ModuleKey, LucideIcon> = { address_check: MapPinCheck, returns_portal: RotateCcw, channels: Store, shop_widgets: Clock3, analytics: Gauge, api: Plug, customer_comms: Mail, purchasing: ClipboardList, b2b: Container, fba: PackageCheck, ai: Bot, tracking: PackageSearch, apps: Blocks, profit: TrendingUp, production: Factory, tracking_page: Sparkles };
const eur = (n: number) => n.toLocaleString(intlLocale(), { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 4 });
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(intlLocale(), { day: "2-digit", month: "2-digit", year: "numeric" }) : "");
const errMsg = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "") : "");

/** Preis in einem Satz: „29,00 € / Monat + 0,015 € je geprüfte Adresse (500 inklusive)“ */
export function priceText(p: ModulePrice, unit: string) {
  const parts = [p.baseFee > 0 && `${eur(p.baseFee)} / Monat`, p.unitFee > 0 && `${eur(p.unitFee)} je ${unit}${p.freeUnits ? ` (${fmtNumber(p.freeUnits)} pro Monat inklusive)` : ""}`].filter(Boolean);
  const base = parts.join(" + ") || "kostenlos";
  return p.discountPct ? `${base}, abzüglich ${p.discountPct} % Rabatt` : base;
}

/** Kunden: Module mit Paketen ansehen, testen/buchen, wechseln, kündigen */
export function ModulesPage() {
  const { tenantId, role } = useTenant();
  const q = useQuery({ queryKey: ["modules", tenantId], queryFn: () => api.modules.list(tenantId) });
  return (
    <div className="pb-16">
      <PageHeader title={t("Module")} description={t("Zusatzfunktionen für euer Konto – jederzeit testen, monatlich kündbar. Nach dem Buchen sofort freigeschaltet.")} />
      <div className="px-4 md:px-8 space-y-6 max-w-5xl">
        <BundlesSection canBook={canManage(role)} />
        {q.isLoading ? [0, 1].map((i) => <Skeleton key={i} className="h-72" />) : q.data?.map((m) => <ModuleSection key={m.key} m={m} canBook={canManage(role)} />)}
      </div>
    </div>
  );
}

function StatusBadge({ m }: { m: TenantModule }) {
  if (m.cancelAt && m.access) return <Badge tone="warning">{t("gekündigt zum")} {day(m.cancelAt)}</Badge>;
  switch (m.phase) {
    case "trial": return <Badge tone="info">{t("Testphase bis")} {day(m.trialEndsAt)}</Badge>;
    case "paid": return <Badge tone="success">{t("gebucht")}</Badge>;
    case "free": return <Badge tone="success">{t("freigeschaltet")}</Badge>;
    case "ended": return <Badge>{t("beendet")}</Badge>;
    default: return null;
  }
}

function ModuleSection({ m, canBook }: { m: TenantModule; canBook: boolean }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const [choose, setChoose] = useState<ModulePackageInfo | null>(null);
  const Icon = ICON[m.key] ?? Puzzle;
  const refresh = () => void qc.invalidateQueries({ queryKey: ["modules", tenantId] });
  const cancel = useMutation({
    mutationFn: () => api.modules.cancel(tenantId, m.key),
    onSuccess: (r) => { toast.success("Gekündigt", { description: `„${m.name}“ läuft bis ${day(r.cancelAt)} weiter.` }); refresh(); },
    onError: (e) => toast.error("Nicht gekündigt", { description: errMsg(e) }),
  });
  const daysLeft = m.phase === "trial" && m.trialEndsAt ? Math.max(0, Math.ceil((new Date(m.trialEndsAt).getTime() - Date.now()) / 86_400_000)) : null;
  const allFeatures = Object.entries(m.featureLabels);
  const visible = m.packages.filter((p) => !p.archived || p.id === m.packageId);

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-start gap-3">
        <span className="size-10 shrink-0 rounded-xl bg-surface-2 grid place-items-center"><Icon className="size-5 text-fg-muted" /></span>
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold text-[15px] flex flex-wrap items-center gap-2">{m.name} <StatusBadge m={m} />{m.bundle && <Badge tone="info">{t("über Paket „")}{m.bundle.name}“</Badge>}</h2>
          <p className="text-[13px] text-fg-muted mt-0.5">{t(m.description)}</p>
        </div>
        {canBook && m.access && !m.cancelAt && m.phase !== "free" && !m.bundle && (
          <Button size="sm" variant="ghost" loading={cancel.isPending} onClick={() => window.confirm(m.phase === "trial" ? `„${m.name}“ kündigen? Es endet am ${day(m.trialEndsAt)} – es entstehen keine Kosten.` : `„${m.name}“ zum Monatsende kündigen?`) && cancel.mutate()}>{t("Kündigen")}</Button>
        )}
      </div>

      {daysLeft != null && !m.cancelAt && (
        <p className="mt-3 text-[12.5px] flex items-start gap-1.5 text-fg-muted">
          <Clock className="size-3.5 mt-0.5 shrink-0" />
          <span>{t("Noch")} {daysLeft} {daysLeft === 1 ? t("Tag") : t("Tage")} {t("kostenlos. Danach läuft das Paket automatisch kostenpflichtig weiter – bis zum")} {day(m.trialEndsAt)} {t("kostenlos kündbar.")}</span>
        </p>
      )}
      {m.pendingPackageName && m.pendingFrom && <p className="mt-2 text-[12.5px] text-fg-muted">{t("Wechsel auf „")}{m.pendingPackageName}{t("“ zum")} {day(m.pendingFrom)} {t("geplant.")}</p>}

      {/* Pakete nebeneinander: Preis, enthaltene Funktionen, Aktion */}
      <div className={cx("mt-4 grid gap-3", visible.length >= 3 ? "md:grid-cols-3" : "md:grid-cols-2")}>
        {visible.map((p) => {
          const current = m.access && p.id === m.packageId;
          const pending = p.id === m.pendingPackageId;
          return (
            <div key={p.id} className={cx("rounded-xl border p-4 flex flex-col", current ? "border-fg/40 bg-surface-2/40" : "border-border")}>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-[14px]">{p.name}</h3>
                {current && <Badge tone="success">{t("aktuell")}</Badge>}
                {pending && <Badge tone="info">{t("ab")} {day(m.pendingFrom)}</Badge>}
              </div>
              {p.description && <p className="text-[12.5px] text-fg-muted mt-0.5">{t(p.description)}</p>}
              <p className="mt-3 text-[13px] font-medium">
                {current && m.bundle ? <>{t("im Paket „")}{m.bundle.name}{t("“ enthalten")}{m.price.unitFee > 0 ? ` · ${priceText({ ...m.price, baseFee: 0 }, m.unit)}` : ""}</> : priceText(current ? m.price : p.price, m.unit)}
                {current && m.price.special && <span className="ml-1.5 text-[11.5px] font-normal text-success">{t("Sonderkondition")}</span>}
              </p>
              <p className="text-[11.5px] text-fg-subtle">{t("zzgl. USt. · monatlich kündbar")}{!m.access && m.trialAvailable && p.trialDays ? t(" · {trialDays} Tage kostenlos testen", { trialDays: p.trialDays }) : ""}</p>
              <ul className="mt-3 space-y-1.5 text-[12.5px] flex-1">
                {allFeatures.map(([f, label]) => {
                  const on = p.features.includes(f);
                  return (
                    <li key={f} className={cx("flex items-start gap-1.5", !on && "text-fg-subtle")}>
                      {on ? <Check className="size-3.5 mt-0.5 shrink-0 text-success" /> : <Minus className="size-3.5 mt-0.5 shrink-0" />}
                      {t(label)}
                    </li>
                  );
                })}
              </ul>
              <div className="mt-4">
                {!canBook ? null : m.bundle ? (
                  <span className="text-[12px] text-fg-subtle">{current ? t("im Paket „{name}“", { name: m.bundle.name }) : t("Änderungen über das Paket")}</span>
                ) : current && !m.cancelAt && !m.pendingPackageId ? (
                  <span className="text-[12px] text-fg-subtle">{m.phase === "free" ? t("für euch freigeschaltet") : t("gebucht")}</span>
                ) : !p.bookable ? (
                  <span className="text-[12px] text-fg-subtle">{t("Demnächst buchbar – sprecht uns gerne an.")}</span>
                ) : (
                  <Button size="sm" variant={m.access && !current ? "secondary" : "primary"} onClick={() => setChoose(p)}>
                    {current ? (m.cancelAt ? t("Kündigung zurücknehmen") : t("Weiter nutzen")) : m.access ? t("Zu „{name}“ wechseln", { name: p.name }) : m.trialAvailable && p.trialDays ? t("{trialDays} Tage testen", { trialDays: p.trialDays }) : t("Jetzt buchen")}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {!canBook && !m.access && <p className="mt-3 text-[12px] text-fg-subtle">{t("Buchen können Administratoren eures Kontos.")}</p>}
      {m.access && (
        <p className="mt-3 text-[12px] text-fg-muted">
          {t("Diesen Monat:")} <span className="font-medium text-fg tabular-nums">{fmtNumber(m.usageThisMonth)}</span> {m.usageThisMonth === 1 ? m.unit : m.unitPlural}
        </p>
      )}
      {choose && <BookDialog m={m} p={choose} onClose={() => setChoose(null)} onDone={refresh} />}
    </Card>
  );
}

/** Buchen / Wechseln mit ausdrücklicher Bestätigung von Preis und Bedingungen */
function BookDialog({ m, p, onClose, onDone }: { m: TenantModule; p: ModulePackageInfo; onClose: () => void; onDone: () => void }) {
  const { tenantId } = useTenant();
  const [accepted, setAccepted] = useState(false);
  const current = m.packages.find((x) => x.id === m.packageId);
  const isSwitch = m.access && !!current && current.id !== p.id;
  const isUpgrade = isSwitch && (p.price.baseFee > current!.price.baseFee || (p.price.baseFee === current!.price.baseFee && p.features.length >= current!.features.length));
  const trial = !m.access && m.trialAvailable && p.trialDays > 0;
  const book = useMutation({
    mutationFn: () => api.modules.book(tenantId, m.key, p.id),
    onSuccess: (r) => {
      const msg: Record<typeof r.change, [string, string]> = {
        booked: r.phase === "trial" ? ["Testphase gestartet", `„${m.name} – ${p.name}“ ist sofort freigeschaltet – kostenlos bis ${day(r.trialEndsAt)}.`] : ["Gebucht", `„${m.name} – ${p.name}“ ist sofort freigeschaltet.`],
        reactivated: ["Weiter gebucht", `„${m.name} – ${p.name}“ läuft weiter.`],
        upgraded: ["Gewechselt", `„${p.name}“ gilt ab sofort.`],
        switched: ["Gewechselt", `„${p.name}“ gilt ab sofort.`],
        downgrade_scheduled: ["Wechsel geplant", `„${p.name}“ gilt ab ${day(r.pendingFrom)} – bis dahin bleibt alles wie gebucht.`],
      };
      toast.success(msg[r.change][0], { description: msg[r.change][1] });
      onDone();
      onClose();
    },
    onError: (e) => toast.error("Nicht gebucht", { description: errMsg(e) }),
  });
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-[70] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(480px,calc(100vw-32px))] rounded-2xl border border-border bg-surface p-6 shadow-2xl animate-pop-in">
          <Dialog.Title className="text-[17px] font-semibold">{isSwitch ? t("Wechsel zu „{name}“", { name: p.name }) : trial ? t("{name} – {name2} testen", { name: m.name, name2: p.name }) : t("{name} – {name2} buchen", { name: m.name, name2: p.name })}</Dialog.Title>
          <Dialog.Description className="mt-1 text-[13px] text-fg-muted">
            {isSwitch ? (isUpgrade || m.phase !== "paid" ? t("Der Wechsel gilt sofort.") : t("Der Wechsel gilt zum Monatsende – bis dahin bleibt euer bisheriges Paket.")) : t("Das Modul ist sofort freigeschaltet.")}
          </Dialog.Description>
          <ul className="mt-4 space-y-2 text-[13px]">
            {trial && <li className="flex gap-2"><CheckCircle2 className="size-4 text-success shrink-0 mt-0.5" /><span><b>{p.trialDays} {t("Tage kostenlos")}</b> {t("– bis zum letzten Testtag ohne Kosten kündbar.")}</span></li>}
            <li className="flex gap-2"><CheckCircle2 className="size-4 text-success shrink-0 mt-0.5" /><span>{trial ? t("Danach automatisch kostenpflichtig: ") : t("Preis: ")}<b>{priceText(p.price, m.unit)}</b>{t(", zzgl. USt.")}</span></li>
            <li className="flex gap-2"><CheckCircle2 className="size-4 text-success shrink-0 mt-0.5" /><span>{t("Monatlich kündbar zum Monatsende. Bei Buchung oder Wechsel im laufenden Monat wird die Grundgebühr anteilig berechnet.")}</span></li>
            <li className="flex gap-2"><CheckCircle2 className="size-4 text-success shrink-0 mt-0.5" /><span>{t("Abrechnung mit eurer monatlichen Logiful-Rechnung.")}</span></li>
          </ul>
          <label className="mt-5 flex items-start gap-2.5 text-[13px] cursor-pointer select-none">
            <input type="checkbox" className="mt-0.5 size-4 accent-current" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
            <span>{t("Ich bestätige Preis und Bedingungen")}{trial ? t(" – nach der Testphase wird das Paket kostenpflichtig, sofern ich nicht vorher kündige") : ""}.</span>
          </label>
          <div className="mt-6 flex justify-end gap-2">
            <Dialog.Close asChild><Button variant="ghost">{t("Abbrechen")}</Button></Dialog.Close>
            <Button variant="primary" disabled={!accepted} loading={book.isPending} onClick={() => book.mutate()}>{isSwitch ? t("Wechseln") : trial ? t("Kostenlos testen") : t("Kostenpflichtig buchen")}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
